/* eslint-disable react-hooks/exhaustive-deps */
// -----------------------------------------------------------------------------
// FollowUpChecklistV2
//
// V2 task PREVIEW surface for the "Follow Up" outcome on OpenAttendedPopup.
// Renders a read-only preview of what V2 checklist items will be assigned
// to this lead when their status transitions to CONSULTATION_ATTENDED.
//
// Sources the preview from `task-templates` (filtered by applies_to_statuses)
// — NOT from `task-checklists` — because those tasks haven't been spawned yet.
// Uses ChecklistItemPreviewRow (read-only) for each template's checklist_items.
//
// Fallback: if no V2 templates target CONSULTATION_ATTENDED OR practice isn't
// in V2 mode, renders the V1 FollowUp form so the user can always set a
// manual reminder.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useEffect, useMemo, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useSelector } from 'src/redux/store';
import {
    Box,
    Typography,
    Stack,
    Skeleton,
    Alert,
    Button,
    Chip,
} from '@mui/material';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import Iconify from 'src/components/Iconify';
import { FollowUp } from 'src/components/tabs';
// Action metadata definitions have moved to the shared actionMeta module.
// Re-exported here for backward compatibility with any external importers.
import { ACTION_META, DEFAULT_ACTION_META } from './AttendedGuidedChecklist/actionMeta';

export { ACTION_META, DEFAULT_ACTION_META };

const UPCOMING_STATUS = 'CONSULTATION_ATTENDED';

export default function FollowUpChecklistV2({ practiceId, leadDetail, handleClose }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const templatesUpdatedAt = useSelector((s) => s?.taskTemplate?.templatesUpdatedAt || 0);

    const fetchTemplates = useCallback(async () => {
        if (!practiceId) return;
        if (!hasFeatureAccess('tasks-v2', practiceId)) {
            setTemplates([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setFetchError(null);
        try {
            const res = await callApi(`/api/v1/practices/${practiceId}/task-templates/?limit=200`);
            let tplData = [];
            if (Array.isArray(res?.data?.results)) {
                tplData = res.data.results;
            } else if (Array.isArray(res?.data)) {
                tplData = res.data;
            }
            setTemplates(tplData);
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'follow_up_checklist_v2_template_fetch' } });
            setFetchError('Failed to load follow-up templates.');
        } finally {
            setLoading(false);
        }
    }, [practiceId]);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates, templatesUpdatedAt]);

    // Resolve this lead's treatment ID so we can prefer treatment-specific templates.
    const leadTreatmentId = useMemo(() => {
        const lt = leadDetail?.lead_treatments?.[0];
        return lt?.treatment_data?.id || lt?.treatment || null;
    }, [leadDetail]);

    // Templates that apply to the upcoming status AND have at least one checklist item AND are active.
    // Selection rule (matches BE behavior):
    //   1. Treatment-specific templates (applies_to_treatment_items includes this lead's treatment) win.
    //   2. If none match, fall back to generic templates (applies_to_treatment_items empty/missing).
    const applicableTemplates = useMemo(() => {
        const candidates = templates.filter((t) => {
            const statuses = Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : [];
            const items = Array.isArray(t.checklist_items) ? t.checklist_items : [];
            const isActive = t.is_active !== false;
            return statuses.includes(UPCOMING_STATUS) && items.length > 0 && isActive;
        });
        if (candidates.length === 0) return [];

        let matched = [];
        if (leadTreatmentId) {
            const treatmentSpecific = candidates.filter((t) => {
                const treatments = Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : [];
                return treatments.length > 0 && treatments.some((id) => String(id) === String(leadTreatmentId));
            });
            if (treatmentSpecific.length > 0) matched = treatmentSpecific;
        }
        if (matched.length === 0) {
            // Generic templates (no specific treatment restriction).
            matched = candidates.filter((t) => {
                const treatments = Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : [];
                return treatments.length === 0;
            });
        }
        return matched.map((t) => ({
            ...t,
            checklist_items: [...t.checklist_items].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
        }));
    }, [templates, leadTreatmentId]);

    const Header = (
        <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em' }}>
                Upcoming follow-up tasks
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em', mt: 0.25 }}>
                {applicableTemplates.length > 0
                    ? 'These will be assigned when you mark this attended.'
                    : 'No predefined V2 tasks configured — schedule a reminder below.'}
            </Typography>
        </Box>
    );

    if (loading) {
        return (
            <Box>
                {Header}
                <Stack spacing={1}>
                    {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: 2 }} />)}
                </Stack>
            </Box>
        );
    }

    if (fetchError) {
        return (
            <Box>
                {Header}
                <Alert severity="warning" action={<Button size="small" onClick={fetchTemplates}>Retry</Button>}>
                    {fetchError}
                </Alert>
            </Box>
        );
    }

    if (applicableTemplates.length === 0) {
        return (
            <Box>
                {Header}
                <FollowUp handleClose={handleClose} />
            </Box>
        );
    }

    return (
        <Box>
            {Header}
            <Stack spacing={2}>
                {applicableTemplates.map((template) => {
                    const itemCount = template.checklist_items.length;
                    return (
                        <Box
                            key={template.id}
                            sx={{
                                bgcolor: '#F5F5F7',
                                borderRadius: '10px',
                                p: 2,
                            }}
                        >
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '7px',
                                        bgcolor: 'rgba(0, 113, 227, 0.10)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <Iconify icon="solar:checklist-minimalistic-bold-duotone" width={15} height={15} sx={{ color: '#0071e3' }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.012em' }}>
                                        {template.name}
                                    </Typography>
                                </Stack>
                                <Chip
                                    label={`${itemCount} ${itemCount === 1 ? 'step' : 'steps'}`}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        bgcolor: '#ffffff',
                                        color: '#6e6e73',
                                        letterSpacing: '-0.005em',
                                        border: '1px solid rgba(0,0,0,0.10)',
                                        '& .MuiChip-label': { px: 0.875 },
                                    }}
                                />
                            </Stack>
                            <Box sx={{
                                bgcolor: '#ffffff',
                                borderRadius: '8px',
                                p: 2,
                                position: 'relative',
                            }}>
                                {template.checklist_items.map((item, idx) => {
                                    const meta = ACTION_META[item.item_type] || DEFAULT_ACTION_META;
                                    const isLast = idx === template.checklist_items.length - 1;
                                    const title = item.title || item.name || '(untitled step)';
                                    return (
                                        <Box
                                            key={item.id || item.template_item_id || `${template.id}-${item.sort_order}`}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 1.25,
                                                pb: isLast ? 0 : 1.75,
                                                position: 'relative',
                                            }}
                                        >
                                            {/* Vertical connector line */}
                                            {!isLast && (
                                                <Box sx={{
                                                    position: 'absolute',
                                                    left: '13px',
                                                    top: '28px',
                                                    bottom: '-2px',
                                                    width: '2px',
                                                    bgcolor: 'rgba(0,0,0,0.06)',
                                                    borderRadius: '1px',
                                                }} />
                                            )}
                                            {/* Action-type icon dot */}
                                            <Box sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: '50%',
                                                bgcolor: `${meta.color}1A`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                border: `2px solid ${meta.color}33`,
                                                position: 'relative',
                                                zIndex: 1,
                                            }}>
                                                <Iconify icon={meta.icon} width={14} height={14} sx={{ color: meta.color }} />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.25 }}>
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.01em' }}>
                                                        {title}
                                                    </Typography>
                                                    <Box sx={{
                                                        fontSize: '10px',
                                                        fontWeight: 600,
                                                        color: meta.color,
                                                        bgcolor: `${meta.color}14`,
                                                        px: 0.75,
                                                        py: 0.125,
                                                        borderRadius: '4px',
                                                        letterSpacing: '0.02em',
                                                        textTransform: 'uppercase',
                                                    }}>
                                                        {meta.label}
                                                    </Box>
                                                </Stack>
                                                {item.description && (
                                                    <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em', mt: 0.25, lineHeight: 1.4 }}>
                                                        {item.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
            <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mt: 2,
                p: 1.5,
                bgcolor: 'rgba(0, 113, 227, 0.06)',
                borderRadius: '8px',
            }}>
                <Iconify icon="solar:info-circle-bold-duotone" width={16} height={16} sx={{ color: '#0071e3', mt: 0.125, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '12px', color: '#1d1d1f', letterSpacing: '-0.005em', lineHeight: 1.45 }}>
                    Preview only — these tasks will be created when this lead transitions to attended status.
                </Typography>
            </Box>
        </Box>
    );
}

FollowUpChecklistV2.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadDetail: PropTypes.object,
    handleClose: PropTypes.func,
};

FollowUpChecklistV2.defaultProps = {
    leadDetail: null,
    handleClose: () => {},
};
